import {
  Body,
  Container,
  Column,
  Head,
  Hr,
  Html,
  Img,
  Link,
  Preview,
  Row,
  Section,
  Text,
} from '@react-email/components';
import { BaseEmailTemplateProps } from './type';
import { formatMoney } from "@/lib/money";


type AbandonedCheckoutProps = BaseEmailTemplateProps<any, any>;

export const UTM_PARAMETERS = {
  utmSource: 'checkout_recovery',
  utmMedium: 'email',
  utmCampaign: 'automatic_abandoned_checkout',
};

export const AbandonedCheckout = ({
  userVariables,
  contextVariables,
  emailMessages,
  locale,
  utmParameters = UTM_PARAMETERS,
}: AbandonedCheckoutProps & {
  emailMessages: any;
  locale: string;
  utmParameters?: Record<string, string>;
}) => {
  const { checkoutSession } = contextVariables;
  const { body } = userVariables || {};

  console.log('in email template');
  console.log('userVariables', userVariables);
  console.log('contextVariables', JSON.stringify(contextVariables, null, 2));
  console.log('emailMessages', emailMessages);
  console.log('locale', locale);
  console.log('utmParameters', utmParameters);

  // Hide GotchaClub items predicate function => Todo: make this configurable
  const isGotchaClubItem = (item: any) => {
    return item.product.name.toLowerCase().includes('gotcha club');
  };

  // Add UTM parameters to checkout URL
  const checkoutUrl = new URL(checkoutSession?.checkoutUrl || '');
  Object.entries(utmParameters).forEach(([key, value]) => {
    checkoutUrl.searchParams.append(key, value);
  });

  // Get the order summary based on selected currency or fallback to first presentment currency
  const orderSummary =
    checkoutSession?.orderSummaries?.find(
      (summary: any) => summary.currency === checkoutSession?.selectedPresentmentCurrency,
    ) ?? checkoutSession?.orderSummaries?.[0];

  const currency = orderSummary?.currency || checkoutSession?.store?.presentmentCurrencies?.[0];

  return (
    <Html>
      <Head />
      <Preview>{emailMessages.preview}</Preview>

      <Body style={main}>
        <Container style={container}>
          {/* Header Section */}
          <Section style={header}>
            <Text style={storeName}>{checkoutSession?.store?.name}</Text>
          </Section>

          {/* Message Section */}
          <Section style={messageSection}>
            <div dangerouslySetInnerHTML={{ __html: body }} style={messageText} />
          </Section>

          {/* Products Section */}
          <Section style={productSection}>
            <Text style={sectionTitle}>{emailMessages.cart_title}</Text>
            {orderSummary?.items!.filter((item: any) => !isGotchaClubItem(item)).map((item: any) => (
              <Row key={item.id} style={productRow}>
                <Column style={{ width: '80px' }}>
                  <Img
                    src={item.variant.imageUrl}
                    width="80"
                    height="80"
                    alt={item.product.name}
                    style={productImage}
                  />
                </Column>
                <Column style={productDetails}>
                  <Text style={productTitle}>{item.product.name}</Text>
                  <Text style={productVariant}>{item.variant.name}</Text>
                  <Text style={productQuantity}>
                    {emailMessages.quantity}: {item.quantity}
                  </Text>
                  {item.adjustments?.length > 0 && (
                    <Text style={promotionText}>{item.adjustments[0].description}</Text>
                  )}
                  {item.recurring && (
                    <Text style={recurringText}>
                      {emailMessages.recurring.replace('{interval}', item.interval)}
                      {item.subscriptionSettings?.trial &&
                        emailMessages.trial_period
                          .replace('{duration}', item.subscriptionSettings.trial.duration)
                          .replace('{type}', item.subscriptionSettings.trial.durationType)}
                    </Text>
                  )}
                </Column>
                <Column style={priceColumn}>
                  {/* Tax included in price was taken out of adjustedAmount: given back so it does not read as a discount (#TAG-3345). */}
                  {item.adjustedAmount + (item.includedTaxAmount ?? 0) !== item.amount ? (
                    <>
                      <Text style={priceStrike}>{formatMoney(item.amount as number, currency, locale)}</Text>
                      <Text style={price}>
                        {formatMoney(item.adjustedAmount + (item.includedTaxAmount ?? 0), currency, locale)}
                      </Text>
                    </>
                  ) : (
                    <Text style={price}>{formatMoney(item.amount as number, currency, locale)}</Text>
                  )}
                </Column>
              </Row>
            ))}
          </Section>

          <Hr style={divider} />

          {/* Order Summary */}
          <Section style={summarySection}>
            <Row>
              <Column>
                <Text style={summaryLabel}>{emailMessages.subtotal}</Text>
              </Column>
              <Column style={summaryValueColumn}>
                <Text style={summaryValue}>
                  {formatMoney(orderSummary?.subtotalAmount || 0, currency, locale)}
                </Text>
              </Column>
            </Row>
            {(orderSummary?.lineItemsPromotionAmount || 0) > 0 && (
              <Row>
                <Column>
                  <Text style={summaryLabel}>{emailMessages.discounts}</Text>
                </Column>
                <Column style={summaryValueColumn}>
                  <Text style={summaryValue}>
                    -{formatMoney(orderSummary?.lineItemsPromotionAmount || 0, currency, locale)}
                  </Text>
                </Column>
              </Row>
            )}
            <Row style={totalRow}>
              <Column>
                <Text style={totalLabel}>{emailMessages.total}</Text>
              </Column>
              <Column style={summaryValueColumn}>
                <Text style={totalValue}>
                  {formatMoney(orderSummary?.totalAdjustedAmount || 0, currency, locale)}
                </Text>
              </Column>
            </Row>
          </Section>

          {/* CTA Section */}
          <Section style={ctaSection}>
            <Link href={checkoutUrl.toString()} style={ctaButton}>
              {emailMessages.cta_button}
            </Link>
          </Section>

          {/* Footer */}
          <Text style={footerText}>{emailMessages.footer_text}</Text>
        </Container>
      </Body>
    </Html>
  );
};

const emerald = {
  50: '#f0fdf4',
  500: '#10b981',
  600: '#059669',
};

const main = {
  fontFamily:
    '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif',
  backgroundColor: '#ffffff',
};

const container = {
  margin: '0 auto',
  padding: '40px 20px',
  width: '600px',
  maxWidth: '100%',
};

const header = {
  marginBottom: '32px',
};

const storeName = {
  fontSize: '24px',
  lineHeight: '1.3',
  fontWeight: '700',
  color: '#111827',
  margin: '0',
  textAlign: 'center' as const,
};

const messageSection = {
  marginBottom: '32px',
};

const messageText = {
  fontSize: '16px',
  lineHeight: '1.5',
  color: '#374151',
  margin: '0',
  textAlign: 'center' as const,
};

const productSection = {
  marginBottom: '32px',
};

const sectionTitle = {
  fontSize: '18px',
  fontWeight: '600',
  color: '#111827',
  marginBottom: '16px',
};

const productRow = {
  marginBottom: '24px',
};

const productImage = {
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
};

const productDetails = {
  paddingLeft: '16px',
};

const productTitle = {
  fontSize: '16px',
  fontWeight: '600',
  color: '#111827',
  margin: '0 0 4px 0',
};

const productVariant = {
  fontSize: '14px',
  color: '#6b7280',
  margin: '0 0 4px 0',
};

const productQuantity = {
  fontSize: '14px',
  color: '#6b7280',
  margin: '0',
};

const priceColumn = {
  width: '100px',
  textAlign: 'right' as const,
};

const price = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#111827',
  margin: '0',
};

const priceStrike = {
  fontSize: '14px',
  textDecoration: 'line-through',
  color: '#6b7280',
  margin: '0 0 4px 0',
};

const promotionText = {
  fontSize: '14px',
  color: emerald[500],
  margin: '4px 0 0 0',
};

const recurringText = {
  fontSize: '14px',
  color: '#6366f1',
  margin: '4px 0 0 0',
};

const divider = {
  borderTop: '1px solid #e5e7eb',
  margin: '32px 0',
};

const summarySection = {
  marginBottom: '32px',
};

const summaryLabel = {
  fontSize: '14px',
  color: '#6b7280',
  margin: '0',
};

const summaryValue = {
  fontSize: '14px',
  color: '#111827',
  margin: '0',
};

const summaryValueColumn = {
  width: '100px',
  textAlign: 'right' as const,
};

const totalRow = {
  marginTop: '12px',
  borderTop: '1px solid #e5e7eb',
  paddingTop: '12px',
};

const totalLabel = {
  fontSize: '16px',
  fontWeight: '600',
  color: '#111827',
  margin: '0',
};

const totalValue = {
  fontSize: '16px',
  fontWeight: '600',
  color: '#111827',
  margin: '0',
};

const ctaSection = {
  textAlign: 'center' as const,
  marginBottom: '32px',
};

const ctaButton = {
  backgroundColor: emerald[500],
  color: '#ffffff',
  fontSize: '16px',
  textDecoration: 'none',
  padding: '12px 32px',
  borderRadius: '6px',
  display: 'inline-block',
  fontWeight: '500',
  textAlign: 'center' as const,
};

const footerText = {
  fontSize: '14px',
  color: '#6b7280',
  textAlign: 'center' as const,
  margin: '0',
};
